import { createSelector } from '@reduxjs/toolkit';
import { createStructuredSelector } from 'reselect';

import {
  selectAreRequiredApiCallsPending,
  selectIsAutoCompleteEnabled,
} from 'features/call/call-base/store/selectors';
import { isConferenceEmptySelector } from 'features/call/call-conference/store';
import { isDeafDisconnectedSelector } from 'features/call/call-deaf/store';
import { isAllHearingsDisconnectedSelector } from 'features/call/call-hearing/store';
import { selectIsFullyDisconnected } from 'features/caller-session/store';
import {
  selectIsEmergencyDeafReconnectionNeeded,
  selectIsSpawningEmergencyCall,
} from 'features/emergency/store';
import { selectIsSignMailSessionInProgress } from 'features/signmail/store';
import {
  isRemoteTeamingSelector,
  isTeamingStartedSelector,
} from 'features/teaming/teaming-base/store';

/**
 * Bundles up all of the flags that go into “can-close” logic
 */
export const selectCallCloseParams = createStructuredSelector({
  isConferenceEmpty: isConferenceEmptySelector,
  isDeafDisconnected: isDeafDisconnectedSelector,
  isAllHearingsDisconnected: isAllHearingsDisconnectedSelector,
  areRequiredApiCallsPending: selectAreRequiredApiCallsPending,
  isRemoteTeaming: isRemoteTeamingSelector,
  isTeamingStarted: isTeamingStartedSelector,
  isEmergencyDeafReconnectionNeeded: selectIsEmergencyDeafReconnectionNeeded,
  isSpawningEmergencyCall: selectIsSpawningEmergencyCall,
  isCallerSessionCanEnd: selectIsFullyDisconnected,
  isSignMailSessionInProgress: selectIsSignMailSessionInProgress,
});

/**
 * Can the call be closed if we ignore auto-complete?
 * This is used to determine if we should show the parked notification.
 * Do not use it elsewhere if you are unsure about the implications.
 */
export const selectCanCallBeClosedIgnoringAutoComplete = createSelector(
  selectCallCloseParams,
  (params) => getCallCloseBlockReason(params) === null
);

/**
 * Should the call auto-close? Returns both flag and block reason.
 */
export const selectIsCallCanBeClosed = createSelector(
  selectCallCloseParams,
  selectIsAutoCompleteEnabled,
  (params, isAutoCompleteEnabled) => {
    if (!isAutoCompleteEnabled) {
      return {
        canBeClosed: false,
        reasonWhyCallDidNotAutoClose: 'auto complete disabled',
      };
    }
    const reason = getCallCloseBlockReason(params);
    return {
      canBeClosed: !reason,
      reasonWhyCallDidNotAutoClose: reason || '',
    };
  }
);

/**
 * Show “parked” notification when auto-complete is off
 * but the call would close if it were on.
 */
export const selectShouldShowParkedNotification = createSelector(
  selectCanCallBeClosedIgnoringAutoComplete,
  selectIsAutoCompleteEnabled,
  (canCloseIgnoring, isAutoCompleteEnabled) =>
    !isAutoCompleteEnabled && canCloseIgnoring
);

/**
 * Returns a string reason if the call cannot be closed,
 * or null if it can be closed.
 */
function getCallCloseBlockReason(params: {
  isSignMailSessionInProgress: boolean;
  isCallerSessionCanEnd: boolean;
  isConferenceEmpty: boolean;
  areRequiredApiCallsPending: boolean;
  isRemoteTeaming: boolean;
  isTeamingStarted: boolean;
  isEmergencyDeafReconnectionNeeded: boolean;
  isSpawningEmergencyCall: boolean;
  isDeafDisconnected: boolean;
  isAllHearingsDisconnected: boolean;
}): string | null {
  const checks: [boolean, string][] = [
    [params.isSignMailSessionInProgress, 'sign mail session in progress'],
    [!params.isCallerSessionCanEnd, 'caller session is not fully disconnected'],
    [!params.isConferenceEmpty, 'conference is not empty'],
    [params.areRequiredApiCallsPending, 'connection state history in progress'],
    [
      params.isRemoteTeaming && params.isTeamingStarted,
      'remote teaming is started',
    ],
    [
      params.isEmergencyDeafReconnectionNeeded,
      'emergency deaf reconnection needed',
    ],
    [params.isSpawningEmergencyCall, 'emergency call is spawning'],
    [
      !(params.isDeafDisconnected && params.isAllHearingsDisconnected),
      'not all participants are disconnected',
    ],
  ];

  for (const [condition, reason] of checks) {
    if (condition) {
      return reason;
    }
  }
  return null;
}
